// The legend a control map is exported with (cockpitsheet.ts → sheets.ts →
// exporter.ts): the territorial control inside the frame, in the colours the
// map draws it. Kept apart from the cockpit's sheet host so it can be tested
// without the replay clock and the map modules that host needs.

import type { EntityDto } from './api';
import type { Bbox, LegendEntry } from './exporter';
import { factionColors } from './territory';

/** The bounds of a GeoJSON geometry's coordinates. */
export function geometryBbox(geometry: EntityDto['geometry']): Bbox | null {
  if (!geometry) return null;
  let w = Infinity; let s = Infinity; let e = -Infinity; let n = -Infinity;
  const walk = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      w = Math.min(w, c[0]); e = Math.max(e, c[0]);
      s = Math.min(s, c[1]); n = Math.max(n, c[1]);
      return;
    }
    for (const part of c) walk(part);
  };
  walk((geometry as { coordinates?: unknown }).coordinates);
  return Number.isFinite(w) ? [w, s, e, n] : null;
}

const overlaps = (a: Bbox, b: Bbox): boolean =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

/** One entry per holder slot whose territory reaches into `bbox`: held ground
 *  first, contested ground hatched, a control map's key and nothing else. */
export function controlLegend(all: EntityDto[], bbox: Bbox): LegendEntry[] {
  const colors = factionColors(all);
  const seen = new Map<string, LegendEntry>();
  for (const e of all) {
    if (e.type !== 'territory') continue;
    const bb = geometryBbox(e.geometry);
    if (!bb || !overlaps(bb, bbox)) continue;
    const faction = String(e.attrs.faction ?? '').trim();
    if (!faction) continue;
    const contested = e.attrs.status === 'contested';
    const by = String(e.attrs.contested_by ?? '').trim();
    const label = contested ? `${faction} — contested${by ? ` by ${by}` : ''}` : `${faction} — control`;
    if (!seen.has(label)) seen.set(label, { label, color: colors[faction] ?? '#8a6d3b', hatch: contested });
  }
  return [...seen.values()].sort((a, b) => Number(a.hatch) - Number(b.hatch) || a.label.localeCompare(b.label));
}
